import { useJobillaOrgId, useWhatsAppConversations } from '@/hooks'
import { CheckboxFilter, logger, MainNav } from '@/ui'
import { useWhatsAppEnabled } from '@/ui/utilities/hooks/use-whatsapp-enabled'
import { WhatsAppThread } from '@/ui/views/campaign-card/candidate-card/whatsapp-thread'
import { Link, useParams } from '@tanstack/react-router'
import { MessageCircleIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ConversationList } from './conversation-list'

export type SelectedConversation = {
	candidateId: string
	campaignId: string
	candidateName: string | null
	campaignTitle: string | null
}

export function WhatsAppInbox() {
	const { t } = useTranslation('common')
	const { companyId } = useParams({ from: '/_auth/company/$companyId/whatsapp/' })
	const whatsAppEnabled = useWhatsAppEnabled()
	const organisationId = useJobillaOrgId()

	const [needsReplyOnly, setNeedsReplyOnly] = useState(false)
	const [unreadOnly, setUnreadOnly] = useState(false)
	const [selected, setSelected] = useState<SelectedConversation | null>(null)

	// Campaign filter dropdown intentionally omitted for v1: tas has no reusable
	// single-campaign selector to compose here. See task-4-report.md.
	// Freshness is poll-based (30s in useWhatsAppConversations); a dedicated per-org
	// inbound-message broadcast would make this real-time and is a backend follow-up.
	const { conversations, isLoading, needsReplyCount, unreadConversationsCount } = useWhatsAppConversations(
		{ needsReply: needsReplyOnly, unread: unreadOnly },
		{ enabled: whatsAppEnabled },
	)

	// One view event per visit, sent once the first page of data is in so the
	// backlog counts describe what the recruiter actually walked into.
	const hasTrackedView = useRef(false)
	useEffect(() => {
		if (!whatsAppEnabled || isLoading || hasTrackedView.current) {
			return
		}
		hasTrackedView.current = true
		logger.track('WhatsApp Inbox: Viewed', {
			category: 'whatsapp',
			data: {
				organisation_id: organisationId,
				needs_reply_count: needsReplyCount,
				unread_conversations_count: unreadConversationsCount,
			},
		})
	}, [whatsAppEnabled, isLoading, organisationId, needsReplyCount, unreadConversationsCount])

	const toggleFilter = (filter: 'needs_reply' | 'unread', enabled: boolean) => {
		if (filter === 'needs_reply') {
			setNeedsReplyOnly(enabled)
		} else {
			setUnreadOnly(enabled)
		}
		logger.track('WhatsApp Inbox: Filter Toggled', {
			category: 'whatsapp',
			data: { organisation_id: organisationId, filter, enabled },
		})
	}

	if (!whatsAppEnabled) {
		return null
	}

	return (
		<MainNav activeMainNavLink="whatsapp">
			<div className="bg-grey-50 flex h-[var(--screen-height)] flex-col">
				<div className="flex min-h-0 flex-1 flex-col space-y-4 px-10 py-8">
					<h1 className="text-xl font-semibold text-grey-900">{t('whatsappInbox.title')}</h1>

					<div className="flex items-center gap-3">
						<CheckboxFilter
							checked={needsReplyOnly}
							onChange={(checked) => toggleFilter('needs_reply', checked)}
							label={t('whatsappInbox.needsReply')}
						/>
						<CheckboxFilter
							checked={unreadOnly}
							onChange={(checked) => toggleFilter('unread', checked)}
							label={t('whatsappInbox.unread')}
						/>
					</div>

					<div className="flex min-h-0 flex-1 gap-4">
						<div className="w-96 max-w-[40%] flex-shrink-0 overflow-y-auto rounded-md border border-grey-100 bg-white">
							<ConversationList
								conversations={conversations}
								isLoading={isLoading}
								selectedCandidateId={selected?.candidateId ?? null}
								onSelect={(conversation) => {
									setSelected({
										candidateId: conversation.candidateId,
										// Row only fires onSelect when campaign_id is present.
										campaignId: conversation.campaignId ?? '',
										candidateName: conversation.candidateName,
										campaignTitle: conversation.campaignTitle,
									})
									logger.track('WhatsApp Inbox: Conversation Opened', {
										category: 'whatsapp',
										data: {
											organisation_id: organisationId,
											campaign_id: conversation.campaignId,
											candidate_id: conversation.candidateId,
											needs_reply: conversation.needsReply,
											had_unread: conversation.unreadCount > 0,
										},
									})
								}}
							/>
						</div>

						<div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-md border border-grey-100 bg-white">
							{selected
								? (
									<>
										<div className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-grey-200 bg-white px-5 py-3">
											<div className="min-w-0">
												<p className="truncate font-semibold text-grey-900">
													{selected.candidateName ?? t('whatsappInbox.anonymisedCandidate')}
												</p>
												{selected.campaignTitle
													? (
														<p className="truncate text-xs text-grey-500">
															{selected.campaignTitle}
														</p>
													)
													: null}
											</div>
											<Link
												to="/company/$companyId/$campaignId/candidates/$candidateId"
												params={{
													companyId,
													campaignId: selected.campaignId,
													candidateId: selected.candidateId,
												}}
												onClick={() =>
													logger.track('WhatsApp Inbox: Candidate Opened in Campaign', {
														category: 'whatsapp',
														data: {
															organisation_id: organisationId,
															campaign_id: selected.campaignId,
															candidate_id: selected.candidateId,
														},
													})}
												className="flex-shrink-0 text-sm font-medium text-purple-600 hover:text-purple-800"
											>
												{t('whatsappInbox.openCandidate')}
											</Link>
										</div>
										<div className="min-h-0 flex-1">
											<WhatsAppThread
												key={`${selected.candidateId}:${selected.campaignId}`}
												candidate={{ id: selected.candidateId }}
												campaignId={selected.campaignId}
												organisationId={organisationId}
												trackingSource="inbox"
											/>
										</div>
									</>
								)
								: (
									<div className="flex h-full flex-col items-center justify-center p-8 text-grey-400">
										<MessageCircleIcon className="mb-3 size-12 opacity-50" />
										<p className="text-sm">{t('whatsappInbox.selectConversation')}</p>
									</div>
								)}
						</div>
					</div>
				</div>
			</div>
		</MainNav>
	)
}
